import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Kicker, Reveal } from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { useSiteData } from "@/context/SiteData";
import { startCheckout, formatPrice, apiErr } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

export default function Pricing() {
  usePageMeta(
    "Pricing | Diamond's Divine Arts",
    "Pricing for prints, original artwork, custom commissions, and one-on-one consultations at Diamond's Divine Arts."
  );

  const { pricing } = useSiteData();
  const [buyingId, setBuyingId] = useState(null);

  const buy = async (tier) => {
    setBuyingId(tier.id);
    try {
      const { checkout_url } = await startCheckout({ item_type: "pricing", item_id: tier.id });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(apiErr(e, "Could not start checkout"));
      setBuyingId(null);
    }
  };

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-20 pt-16 md:pb-24 md:pt-24" data-testid="pricing-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>Pricing</Kicker>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
              Simple, honest <span className="italic text-[#9A7A3F]">pricing</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              Clear starting points for every offering. Every commission is quoted individually —
              begin with a conversation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-28" data-testid="pricing-cards-section">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8 xl:grid-cols-4">
          {pricing.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.08} className="h-full">
              <div
                className="flex h-full flex-col rounded-sm border border-[#D4AF37]/30 bg-[#F4EFE6]/60 p-8 transition duration-300 hover:-translate-y-1.5 hover:border-[#B89759]/60 hover:shadow-xl"
                data-testid={`pricing-tier-${tier.id}`}
              >
                <h2 className="font-serif text-2xl text-[#1C1815]">{tier.title}</h2>
                <p className="mt-4 font-serif text-3xl font-light text-[#9A7A3F]">
                  {tier.buyable ? formatPrice(tier.price) : tier.price_label || formatPrice(tier.price)}
                </p>
                {tier.unit && (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">{tier.unit}</p>
                )}
                <p className="mt-5 text-sm font-light leading-relaxed text-[#4A423A]">{tier.description}</p>
                <ul className="mt-6 flex-1 space-y-2.5 border-t border-[#D4AF37]/30 pt-5">
                  {(tier.features || []).map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-[#3D352E]">
                      <Check className="h-3.5 w-3.5 text-[#9A7A3F]" /> {f}
                    </li>
                  ))}
                </ul>
                {tier.buyable && tier.price ? (
                  <button
                    onClick={() => buy(tier)}
                    disabled={buyingId === tier.id}
                    className="btn-gold mt-8 w-full disabled:opacity-60"
                    data-testid={`pricing-buy-${tier.id}`}
                  >
                    {buyingId === tier.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {buyingId === tier.id ? "Starting…" : `Buy Now — ${formatPrice(tier.price)}`}
                  </button>
                ) : (
                  <Link
                    to={tier.cta_to || "/booking"}
                    className={tier.cta_to === "/booking" ? "btn-primary mt-8 w-full" : "btn-outline mt-8 w-full"}
                    data-testid={`pricing-cta-${tier.id}`}
                  >
                    {tier.cta_label || "Learn More"} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-[#D4AF37]/20 bg-[#F4EFE6]/60 py-16" data-testid="pricing-consult-strip">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 md:flex-row md:items-center lg:px-8">
          <Reveal>
            <h2 className="font-serif text-2xl font-normal text-[#1C1815] sm:text-3xl">
              Have questions about a piece or a project?
            </h2>
            <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-[#4A423A]">
              Reach out directly — Natalia personally answers every enquiry about artwork,
              commissions, and pricing.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" className="btn-outline" data-testid="pricing-contact-button">
              Contact the Artist <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
